import { externalLinks } from 'src/externalLinks';
import { SearchResult } from 'src/models/search/_types';
import { useMainNav } from 'src/shared/AppFrame/useMainNav';

interface NavigationItem {
  id: string;
  name: string;
  route?: string;
  reactRoute?: string;
  enabled?: boolean;
  children?: NavigationItem[];
}

/**
 * Get the proper route for a navigation item.
 * - If reactRoute exists, use it directly (it's a React view)
 * - If only route exists, prefix with /embed (it's an Ember/legacy view)
 */
const getNavigationRoute = (item: NavigationItem): string | null => {
  if (item.reactRoute) {
    return item.reactRoute;
  }
  if (item.route) {
    // legacy route has invalid data
    if (item.route == '/dashboard/fbm/schedule/') item.route = '/fbm/schedule/';
    // Legacy routes need /embed prefix
    return `/embed${item.route.startsWith('/') ? '' : '/'}${item.route}`;
  }
  return null;
};

/**
 * Flattens the navigation tree into a searchable list of items.
 * Excludes items that are parents (have children) unless they have their own route.
 * Excludes client-specific tabs.
 */
const flattenNavItems = (
  items: NavigationItem[],
  parentName?: string
): SearchResult[] => {
  const results: SearchResult[] = [];

  for (const item of items) {
    // Skip disabled items
    if (item.enabled === false) continue;

    // Get the route (prefer reactRoute, fallback to embedded route)
    const route = getNavigationRoute(item);

    // If item has a route, add it to results
    if (route) {
      results.push({
        id: item.id,
        name: parentName ? `${parentName} > ${item.name}` : item.name,
        aliases: [item.id],
        type: 'navigation',
        data: {
          route,
          parentName,
        },
      });
    }

    // Recursively process children
    if (item.children && item.children.length > 0) {
      results.push(
        ...flattenNavItems(item.children, item.name)
      );
    }
  }

  return results;
};

/**
 * Hardcoded navigation items that should always be searchable.
 * These include account settings and launcher menu items.
 */
const getHardcodedNavItems = (): SearchResult[] => [
  // Account Settings
  {
    id: 'billing-information',
    name: 'Billing Information',
    aliases: ['billing', 'account', 'payment', 'subscription'],
    type: 'navigation',
    data: {
      route: '/me/account',
      parentName: 'Settings',
    },
  },
  {
    id: 'notification-settings',
    name: 'Notification Settings',
    aliases: ['notifications', 'alerts', 'preferences'],
    type: 'navigation',
    data: {
      route: '/me/notifications',
      parentName: 'Settings',
    },
  },
  // Launcher Menu Items (from ActionsMenu)
  {
    id: 'booking',
    name: 'Booking',
    aliases: ['schedule', 'appointments', 'calendar'],
    type: 'navigation',
    data: {
      route: externalLinks.v3Url('/booking'),
      isExternal: true,
    },
  },
  {
    id: 'leaderboards',
    name: 'Leaderboards',
    aliases: ['rankings', 'competition', 'scores'],
    type: 'navigation',
    data: {
      route: externalLinks.v3Url('/leaderboards'),
      isExternal: true,
    },
  },
  {
    id: 'manage-leaderboards',
    name: 'Manage Leaderboards',
    aliases: ['leaderboard settings', 'leaderboard admin'],
    type: 'navigation',
    data: {
      route: '/leaderboards',
    },
  },
  {
    id: 'packages',
    name: 'Packages',
    aliases: ['memberships', 'bundles'],
    type: 'navigation',
    data: {
      route: externalLinks.v3Url('/packages'),
      isExternal: true,
    },
  },
  {
    id: 'assessments',
    name: 'My Assessments',
    aliases: ['evaluations', 'tests', 'fitness tests'],
    type: 'navigation',
    data: {
      route: '/me/assessments',
    },
  },
  {
    id: 'ipad-account-manager',
    name: 'iPad Account Manager',
    aliases: ['account manager', 'ipad', 'kiosk'],
    type: 'navigation',
    data: {
      route: externalLinks.accountManager(),
      isExternal: true,
    },
  },
  // Quick Actions Menu Items
  {
    id: 'new-client',
    name: 'Add Client',
    aliases: ['new client', 'create client', 'register client', 'add account', 'created account', 'new account'],
    type: 'navigation',
    data: {
      route: '/clients/new',
    },
  },
];

/**
 * Search navigation items by term
 */
const filterNavItems = (flatNavItems: SearchResult[], term: string): SearchResult[] => {
  if (!term || term.length < 2) return [];

  const lowerTerm = term.toLowerCase();

  return flatNavItems.filter((item) => {
    // Search in name
    if (item.name.toLowerCase().includes(lowerTerm)) return true;
    // Search in aliases (item id)
    if (item.aliases?.some((alias) => alias.toLowerCase().includes(lowerTerm)))
      return true;
    return false;
  });
};

/**
 * Hook that provides navigation search functionality.
 * Searches through navigation items the current user has permission to view.
 */
export const useNavigationSearch = () => {
  const navItems = useMainNav();

  const dynamicNavItems = flattenNavItems(navItems as NavigationItem[]);
  const hardcodedNavItems = getHardcodedNavItems();

  // Merge dynamic and hardcoded items, avoiding duplicates by ID
  const dynamicIds = new Set(dynamicNavItems.map((item) => item.id));
  const uniqueHardcodedItems = hardcodedNavItems.filter(
    (item) => !dynamicIds.has(item.id)
  );
  const flatNavItems = [...dynamicNavItems, ...uniqueHardcodedItems];

  const searchNavigation = (term: string): SearchResult[] => {
    return filterNavItems(flatNavItems, term);
  };

  return { searchNavigation, flatNavItems };
};
